<template>
  <article class="bg-slate-50 rounded-2xl border border-slate-200 overflow-hidden print:bg-white print:border-none">
    <!-- Clean Header -->
    <header class="bg-white p-10 border-b border-slate-100 text-center">
      <img
        v-if="school?.logo"
        :src="school.logo"
        alt="School Logo"
        class="w-20 h-20 object-contain rounded-lg mx-auto mb-4"
      />
      <h1 class="text-4xl font-extrabold text-slate-900 tracking-tight uppercase">{{ school?.name }}</h1>
      <p class="text-slate-500 mt-2 font-medium tracking-wide italic">{{ school?.address }}</p>
      <div class="inline-block mt-6 px-8 py-2 bg-slate-900 text-white font-bold text-sm tracking-[0.2em] rounded-md">
        ACADEMIC REPORT CARD
      </div>
    </header>

    <div class="p-10 space-y-10">
      <!-- Profile Grid -->
      <section class="grid grid-cols-1 md:grid-cols-3 gap-8">
        <div class="col-span-2 flex items-center gap-6">
          <div class="w-24 h-24 rounded-2xl bg-slate-900 text-white flex items-center justify-center text-4xl font-black">
            {{ studentName.charAt(0) }}
          </div>
          <div>
            <h2 class="text-3xl font-black text-slate-900 uppercase tracking-tight">{{ studentName }}</h2>
            <p class="text-slate-500 font-bold text-lg">{{ studentClass }}</p>
          </div>
        </div>
        <div class="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm space-y-2">
            <div class="flex justify-between text-sm">
                <span class="text-slate-400 font-bold uppercase text-[10px]">Admission No</span>
                <span class="text-slate-900 font-bold">{{ leftColumnInfo['Admission Number'] }}</span>
            </div>
            <div class="flex justify-between text-sm">
                <span class="text-slate-400 font-bold uppercase text-[10px]">Gender</span>
                <span class="text-slate-900 font-bold capitalize">{{ leftColumnInfo['Gender'] }}</span>
            </div>
            <div class="flex justify-between text-sm">
                <span class="text-slate-400 font-bold uppercase text-[10px]">{{ labels.TERM }}</span>
                <span class="text-slate-900 font-bold">{{ leftColumnInfo['Term'] }}</span>
            </div>
        </div>
      </section>

      <!-- Main Result Table -->
      <section>
        <div class="overflow-x-auto rounded-2xl border border-slate-200 shadow-sm bg-white">
          <table class="w-full text-sm text-left border-collapse">
            <thead>
              <tr class="bg-slate-900 text-white font-black uppercase text-[11px] tracking-widest">
                <th v-for="header in tableHeaders" :key="header" class="px-6 py-5">
                  {{ header }}
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-100">
              <tr v-for="(subject, idx) in subjects" :key="idx" class="hover:bg-slate-50/50 transition-colors">
                <td v-for="(header, hIdx) in tableHeaders" :key="hIdx"
                  class="px-6 py-4 text-slate-700 font-bold"
                  :class="{ 'text-slate-900 font-black italic': hIdx === 0 }">
                  {{ subject[header] }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <!-- Grade Performance -->
      <section class="grid grid-cols-1 lg:grid-cols-4 gap-8">
         <div class="lg:col-span-1 space-y-4">
            <h3 class="text-xs font-black text-slate-400 uppercase tracking-widest">Grading Scale</h3>
            <div class="grid grid-cols-2 gap-2">
                <div v-for="grade in grades" :key="grade.letter" class="flex items-center justify-between bg-white p-2 rounded-lg border border-slate-100">
                    <span class="text-slate-900 font-black">{{ grade.letter }}</span>
                    <span class="text-[10px] text-slate-400 font-bold">{{ grade.minimum }} - {{ grade.maximum }}</span>
                </div>
            </div>
         </div>

         <!-- Performance Comments -->
         <div class="lg:col-span-3 space-y-6">
            <div v-for="(groupFeedbacks, categoryName) in categorizedFeedbacks.groups" :key="categoryName" class="space-y-3">
                <h3 class="text-xs font-black text-slate-400 uppercase tracking-widest">{{ categoryName }} Evaluation</h3>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div v-for="fb in groupFeedbacks" :key="fb.id" class="bg-white p-4 rounded-xl border border-slate-100 shadow-sm flex flex-col justify-between">
                         <div>
                            <span class="text-[10px] font-black text-slate-400 uppercase">{{ fb.label }}</span>
                            <FeedbackRatingInput v-if="isRatingItem(fb)" v-model="fb.rating" :feedback="fb" />
                    <Textarea v-else v-model="fb.value" autoResize class="w-full border-none focus:ring-0 p-0 text-slate-900 font-bold mt-1" />
                         </div>
                         <div class="mt-2 flex justify-end">
                            <Button label="Save" text size="small" class="!p-0 font-black text-slate-900 uppercase text-[10px]" data-testid="btn-results-save-feedback-modern" @click="$emit('save-feedback', fb.feedback_id, fb.id, fb.value, fb.rating)" />
                         </div>
                    </div>
                </div>
            </div>
         </div>
      </section>
    </div>

    <footer class="bg-slate-900 text-white p-10 flex flex-col md:flex-row justify-between items-center gap-6">
      <div class="text-center md:text-left">
        <p class="text-slate-400 font-black uppercase text-[10px] tracking-widest">Resumption Info</p>
        <p class="text-xl font-black italic">{{ nextTermDate }}</p>
      </div>
      <div class="h-16 w-32 border-2 border-dashed border-slate-700 flex items-center justify-center text-slate-600 font-black uppercase text-[10px]">
        SCHOOL STAMP
      </div>
    </footer>
  </article>
</template>

<script setup lang="ts">
import FeedbackRatingInput from './FeedbackRatingInput.vue';
import { isRatingItem } from '@/utils/resultFeedbackRating';
import type { School, StudentResultSummary, Grade } from '@/types/composition-functions';
import { useSchoolLabels } from '@/composables/useSchoolLabels';

const { labels } = useSchoolLabels();

const props = defineProps<{
  school: School;
  studentName: string;
  studentClass: string;
  leftColumnInfo: Record<string, string | number>;
  rightColumnInfo: Record<string, string | number>;
  tableHeaders: string[];
  subjects: Record<string, unknown>[];
  resultSummary: StudentResultSummary;
  grades: Grade[];
  categorizedFeedbacks: { groups: Record<string, Record<string, unknown>[]>; general: Record<string, unknown>[] };
  nextTermDate: string;
}>();

defineEmits(['save-feedback']);
</script>
